import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  NotebookPen,
  Check,
} from "lucide-react";

const empty = { name: "", link: "", username: "", password: "", notes: "" };

export default function Register() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ ...empty });
  const [showPw, setShowPw] = useState(false);
  const [saveState, setSaveState] = useState("idle"); // idle | saving | saved
  const saveTimer = useRef(null);
  const projectsRef = useRef(projects);
  projectsRef.current = projects;

  const load = useCallback(async () => {
    const { data } = await api.get("/projects");
    setProjects(data);
    setSelectedId((cur) => cur ?? (data[0]?.id ?? null));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => projects.find((p) => p.id === selectedId) || null,
    [projects, selectedId]
  );

  const openAdd = () => {
    setForm({ ...empty });
    setEditId(null);
    setDialogOpen(true);
  };

  const openEdit = (p) => {
    setForm({ name: p.name, link: p.link, username: p.username, password: p.password, notes: p.notes });
    setEditId(p.id);
    setDialogOpen(true);
  };

  const saveProject = async () => {
    if (!form.name.trim()) {
      toast.error("Project name is required");
      return;
    }
    try {
      if (editId) {
        const { data } = await api.put(`/projects/${editId}`, form);
        setProjects((ps) => ps.map((p) => (p.id === editId ? data : p)));
        setSelectedId(editId);
      } else {
        const { data } = await api.post("/projects", form);
        setProjects((ps) => [data, ...ps]);
        setSelectedId(data.id);
      }
      toast.success(editId ? "Project updated" : "Project added");
      setDialogOpen(false);
    } catch {
      toast.error("Save failed");
    }
  };

  const removeProject = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects((ps) => ps.filter((p) => p.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("Project deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  // Debounced autosave of the notepad
  const onNotesChange = (value) => {
    setProjects((ps) => ps.map((p) => (p.id === selectedId ? { ...p, notes: value } : p)));
    setSaveState("saving");
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const proj = projectsRef.current.find((p) => p.id === selectedId);
      try {
        await api.put(`/projects/${selectedId}`, { ...proj, notes: value });
        setSaveState("saved");
        setTimeout(() => setSaveState("idle"), 1500);
      } catch {
        setSaveState("idle");
        toast.error("Auto-save failed");
      }
    }, 800);
  };

  const copy = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  return (
    <div className="relative min-h-screen bg-[#F8F9FA] text-slate-900">
      <div className="noise-overlay" />

      <header className="sticky top-0 z-40 border-b border-white/50 bg-white/60 backdrop-blur-2xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <button
              data-testid="back-to-portal-btn"
              onClick={() => navigate("/portal")}
              className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/60 bg-white/70 text-slate-600 transition-colors hover:bg-white"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <p className="font-display text-lg leading-none">Register</p>
              <p className="font-plex text-[11px] text-slate-400">
                Project notes & credentials
              </p>
            </div>
          </div>
          <button
            data-testid="add-project-btn"
            onClick={openAdd}
            className="flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 font-plex text-sm text-white transition-transform hover:-translate-y-0.5"
          >
            <Plus className="h-4 w-4" /> Add project
          </button>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-6 pb-24 pt-10 lg:grid-cols-[320px_1fr]">
        {/* Project list */}
        <aside className="rounded-3xl border border-white/60 bg-white/60 p-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-2xl lg:sticky lg:top-24 lg:h-[calc(100vh-8rem)] lg:overflow-y-auto">
          <p className="mb-3 px-2 font-heading text-sm font-semibold text-slate-500">
            Projects · {projects.length}
          </p>
          {projects.length === 0 && (
            <p className="px-2 py-8 text-center font-plex text-sm text-slate-400">
              No projects yet. Add your first one.
            </p>
          )}
          <div className="space-y-1">
            {projects.map((p) => (
              <div
                key={p.id}
                data-testid={`project-item-${p.id}`}
                onClick={() => setSelectedId(p.id)}
                className={`group flex cursor-pointer items-center justify-between rounded-xl px-3 py-2.5 transition-colors ${
                  selectedId === p.id ? "bg-slate-900 text-white" : "hover:bg-white"
                }`}
              >
                <span className="truncate font-plex text-sm font-medium">{p.name}</span>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                    <button
                      data-testid={`project-menu-${p.id}`}
                      className={`rounded-lg p-1 transition-colors ${
                        selectedId === p.id ? "text-white/70 hover:text-white" : "text-slate-300 hover:text-slate-700"
                      }`}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenuItem data-testid={`edit-project-${p.id}`} onClick={() => openEdit(p)}>
                      <Pencil className="mr-2 h-4 w-4" /> Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid={`delete-project-${p.id}`}
                      className="text-rose-600 focus:text-rose-600"
                      onClick={() => removeProject(p.id)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        </aside>

        {/* Detail + notepad */}
        <section>
          {!selected ? (
            <div className="flex h-full min-h-[50vh] flex-col items-center justify-center rounded-3xl border border-white/60 bg-white/50 text-slate-400 backdrop-blur-2xl">
              <NotebookPen className="mb-3 h-8 w-8" />
              <p className="font-plex text-sm">Select or add a project to start taking notes.</p>
            </div>
          ) : (
            <motion.div
              key={selected.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.35 }}
              className="space-y-6"
            >
              <div className="rounded-3xl border border-white/60 bg-white/60 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-2xl">
                <div className="flex items-start justify-between gap-4">
                  <h1 data-testid="project-title" className="font-display text-3xl">
                    {selected.name}
                  </h1>
                  {selected.link && (
                    <a
                      data-testid="open-project-link"
                      href={selected.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex shrink-0 items-center gap-2 rounded-full bg-slate-900 px-4 py-2 font-plex text-sm text-white transition-transform hover:-translate-y-0.5"
                    >
                      Open <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>

                <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <Field label="Link" value={selected.link} onCopy={() => copy(selected.link, "Link")} mono />
                  <Field label="Username" value={selected.username} onCopy={() => copy(selected.username, "Username")} mono />
                  <div className="rounded-2xl border border-white/60 bg-white/50 p-4">
                    <p className="mb-1 font-plex text-[11px] uppercase tracking-[0.2em] text-slate-400">
                      Password
                    </p>
                    <div className="flex items-center justify-between gap-2">
                      <span data-testid="project-password" className="truncate font-mono text-sm text-slate-800">
                        {selected.password ? (showPw ? selected.password : "••••••••") : "—"}
                      </span>
                      <div className="flex shrink-0 gap-1">
                        <button
                          data-testid="toggle-password"
                          onClick={() => setShowPw((s) => !s)}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                        <button
                          onClick={() => copy(selected.password, "Password")}
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Notepad */}
              <div className="rounded-3xl border border-white/60 bg-white/60 p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] backdrop-blur-2xl">
                <div className="mb-4 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <NotebookPen className="h-4 w-4 text-slate-500" />
                    <p className="font-heading text-sm font-semibold text-slate-600">Notes</p>
                  </div>
                  <span className="flex items-center gap-1 font-plex text-xs text-slate-400">
                    {saveState === "saving" && "Saving…"}
                    {saveState === "saved" && (
                      <>
                        <Check className="h-3.5 w-3.5 text-emerald-500" /> Saved
                      </>
                    )}
                  </span>
                </div>
                <textarea
                  data-testid="project-notes"
                  value={selected.notes || ""}
                  onChange={(e) => onNotesChange(e.target.value)}
                  placeholder="Write anything about this project — ideas, todos, contacts, snippets…"
                  className="min-h-[45vh] w-full resize-none bg-transparent font-plex text-[15px] leading-relaxed text-slate-800 outline-none placeholder:text-slate-300"
                />
              </div>
            </motion.div>
          )}
        </section>
      </main>

      {/* Add / edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="border-white/60 bg-white/90 backdrop-blur-2xl sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editId ? "Edit project" : "New project"}
            </DialogTitle>
            <DialogDescription>
              Store the name, link and login for this project. Notes are saved on the project page.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input data-testid="project-name-input" placeholder="Project name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <Input data-testid="project-link-input" placeholder="https://… (optional)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} />
            <Input data-testid="project-username-input" placeholder="Username (optional)" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} />
            <Input data-testid="project-password-input" placeholder="Password (optional)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button data-testid="save-project-btn" onClick={saveProject} className="rounded-full bg-slate-900">
              {editId ? "Save changes" : "Add project"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onCopy, mono }) {
  return (
    <div className="rounded-2xl border border-white/60 bg-white/50 p-4">
      <p className="mb-1 font-plex text-[11px] uppercase tracking-[0.2em] text-slate-400">{label}</p>
      <div className="flex items-center justify-between gap-2">
        <span className={`truncate text-sm text-slate-800 ${mono ? "font-mono" : "font-plex"}`}>
          {value || "—"}
        </span>
        {value && (
          <button onClick={onCopy} className="shrink-0 rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700">
            <Copy className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}
